// @ts-ignore
import masker from "vue-the-mask/src/masker";
// @ts-ignore
import tokens from "vue-the-mask/src/tokens";
import { isNumber } from "lodash";
import { customTokens } from "@rmp/core/plugins/vueTheMask";

const maskTokens = { ...tokens, ...customTokens };
import ApiLegalEntityCounterparty from "@rmp/core/api/types/counterparty/apiLegalEntityCounterparty";
import ApiLegalPersonCounterparty from "@rmp/core/api/types/counterparty/apiLegalPersonCounterparty";
import { i18n } from "@rmp/core/plugins";
import { isNilOrNaN } from "@rmp/core/utils/isNilOrNaN";
import ApiForeignOrganizationCounterparty from "@rmp/core/api/types/counterparty/apiForeignOrganizationCounterparty";
import ApiPhysicalPersonCounterparty from "@rmp/core/api/types/counterparty/apiPhysicalPersonCounterparty";
import { ApiCounterpartyType } from "@rmp/core/api/types/counterparty/apiCounterpartyType";
import { intervalToDuration } from "date-fns";

export const removeSpaces = (row: string) => row ? row.replace(/\s+/g, "") : "";

export const capitalizeFirstLetter = (row: string) => row && row[0].toUpperCase() + row.slice(1);

export const formatFullName = ({ firstName, lastName, middleName }: {
	firstName: string,
	lastName: string,
	middleName?: string | null
}) => {
	return [lastName, firstName, middleName].filter(x => !!x).join(" ");
};

export const formatFullNameWithPosition = ({ firstName, lastName, middleName, position }: {
	firstName: string,
	lastName: string,
	middleName?: string | null,
	position: string
}) => {
	return `${formatFullName({ firstName, lastName, middleName })}${position ? ", " + position : ""}`;
};

export const formatFullNameWithInitials = ({
	firstName,
	lastName,
	middleName
}: { firstName: string, lastName: string, middleName: string }) => {
	return `${lastName} ${firstName ? firstName.slice(0, 1) + "." : ""} ${middleName ? middleName.slice(0, 1) + "." : ""}`;
};

export const mask = (value: string, mask: string) => {
	return value && masker(value, mask, true, maskTokens);
};

export const unmask = (value: string, mask: string) => {
	return value && masker(value, mask, false, maskTokens);
};

export const formatNumber = (value: number, fractionDigits = 2) => {
	if(!value)
		return value;
	
	const string = fractionDigits ? value.toFixed(fractionDigits) : value.toString();
	
	return string.replace(".", ",").toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

export function formatHours(hours: number): string {
	const ms = Math.round(hours * 3_600_000);
	const { hours: h = 0, minutes: m = 0 } = intervalToDuration({ start: 0, end: ms });
	return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function formatTotalHours(hours: number): string {
	const totalMinutes = Math.round(hours * 60);
	const h = Math.floor(totalMinutes / 60);
	const m = totalMinutes % 60;
	return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export const formatNumberVolume = (value: number, fractionDigits = 2) => {
	if(!value)
		return value;
	
	const string = fractionDigits ? value.toFixed(fractionDigits) : Math.round(value).toString();
	
	return string.replace(".", ",").toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

export const formatCurrency = (value: number | null | undefined, fractionDigits = 2) => {
	const amount = isNilOrNaN(value) ? 0 : value as number;
	
	const string = fractionDigits ? amount.toFixed(fractionDigits) : amount.toString();
	const [integer, decimal] = string.split(".");
	const formattedInteger = integer.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + ` ${i18n.tc("content.rubles")}.`;
	const formattedDecimal = (decimal || "0").padEnd(2, "0") + ` ${i18n.tc("content.kopecks")}.`;
	
	return `${formattedInteger} ${formattedDecimal}`;
};

export const convertNumberToIsoString = (value: number) => {
	return isNumber(value) ? new Date(value).toISOString() : value;
};

export const formatCounterpartyTitle = (counterparty: ApiCounterpartyType) => {
	if(counterparty instanceof ApiLegalEntityCounterparty) {
		return counterparty.shortName;
	} else if(counterparty instanceof ApiLegalPersonCounterparty) {
		return `${i18n.t("common.legalPerson")} "${counterparty.fullName}"`;
	} else if(counterparty instanceof ApiForeignOrganizationCounterparty) {
		return counterparty.longName;
	} else if(counterparty instanceof ApiPhysicalPersonCounterparty) {
		return formatFullName({ ...counterparty });
	} else
		return "";
};

export const splitFullName = (value: string) => {
	let lastName, middleName, firstName;
	const splitted = value.split(" ");
	
	if(splitted.length === 2)
		[lastName, firstName] = splitted;
	else
		[lastName, firstName, middleName] = splitted;
	
	return { lastName, firstName, middleName };
};

export const resolveAgreementNumber = (item: {
	agreementNumber: string | number;
	originalAgreementNumber?: string | null;
}): string | number => item.originalAgreementNumber ?? item.agreementNumber;

export const formatAgreementTitle = (types: any[], number: string | number) => {
	if(types.length > 1)
		return i18n.t("service.agreement.multiServicesAgreement") + ` №${number}`;
	
	const [singleAgreementType] = types;
	return i18n.tc(`service.agreement.title.${singleAgreementType}`) + ` №${number}`;
};

export const formatShortAgreementTitle = (number: string) => {
	return i18n.tc(`titles.agreement`) + ` №${number}`;
};

export const formatServiceTitle = (type: any, number: number | string) => {
	return i18n.tc(`aliases.service.${type}`) + ` №${number}`;
};

export const formatStartOrEndTitle = (type: "start" | "end", value: string | undefined) => {
	return value ? `${i18n.tc(`servicesItem.${type}`)}: ${value}` : undefined;
};

export const formatPhone = (phone: string) => {
	if(!phone || typeof phone !== "string") return "";
	
	const cleaned = phone.replace(/[^\d+]/g, "");
	
	let digits = "";
	if(/^\+7\d{10}$/.test(cleaned)) {
		digits = cleaned.slice(2);
	} else if(/^7\d{10}$/.test(cleaned)) {
		digits = cleaned.slice(1);
	} else if(/^8\d{10}$/.test(cleaned)) {
		digits = cleaned.slice(1);
	} else if(/^\d{10}$/.test(cleaned)) {
		digits = cleaned;
	} else {
		return phone;
	}
	return digits.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, "8 ($1) $2-$3-$4");
};

